import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import Card, { CardHeader } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import TicketAttachmentsInput from '../../components/tickets/TicketAttachmentsInput';
import { useCreateTicketMutation, buildNewTicketFormData } from '../../features/tickets/ticketsApi';
import { newTicketSchema, TICKET_CATEGORIES } from '../../lib/validators/ticketSchema';
import { useToast } from '../../components/ui/Toast';
import { authErrorMessage } from '../../lib/authErrorMessage';

/**
 * POST /investor/support-tickets — multipart, first message included.
 * 201 returns the created ticket with its first message nested; this
 * screen just navigates straight to the detail thread afterward rather
 * than rendering anything from the response itself.
 */
export default function NewTicket() {
  const navigate = useNavigate();
  const toast = useToast();
  const [createTicket, { isLoading }] = useCreateTicketMutation();
  const [attachments, setAttachments] = useState([]);

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(newTicketSchema),
    defaultValues: { subject: '', category: '', body: '', attachments: [] },
  });

  const onSubmit = async (values) => {
    try {
      const formData = buildNewTicketFormData({ ...values, attachments });
      const ticket = await createTicket(formData).unwrap();
      toast.push({ tone: 'success', message: 'Ticket opened — we\'ll get back to you soon.' });
      navigate(`/tickets/${ticket.id}`, { replace: true });
    } catch (err) {
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
        return;
      }
      setError('root', { message: authErrorMessage(err, { fallback: 'Could not open this ticket.' }) });
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <Link to="/tickets" className="text-sm text-accent hover:underline">
        ← Back to support
      </Link>

      <div>
        <h1 className="text-2xl font-semibold text-foreground">New ticket</h1>
        <p className="text-foreground-muted text-sm mt-1">Tell us what's going on and we'll follow up here.</p>
      </div>

      <Card>
        <CardHeader title="Ticket details" />
        <form
          onSubmit={handleSubmit((values) => onSubmit({ ...values, attachments }))}
          className="space-y-5"
          noValidate
        >
          <Input label="Subject" error={errors.subject?.message} {...register('subject')} />

          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1.5">Category</label>
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
                >
                  <option value="">Select a category</option>
                  {TICKET_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              )}
            />
            {errors.category && <p className="mt-1.5 text-xs text-danger">{errors.category.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1.5">Message</label>
            <textarea
              rows={5}
              className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus:border-border-strong"
              placeholder="Describe the issue in as much detail as you can."
              {...register('body')}
            />
            {errors.body && <p className="mt-1.5 text-xs text-danger">{errors.body.message}</p>}
          </div>

          <TicketAttachmentsInput value={attachments} onChange={setAttachments} />

          {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

          <Button type="submit" className="w-full" isLoading={isLoading}>
            Open ticket
          </Button>
        </form>
      </Card>
    </div>
  );
}
